import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTelegram } from '../../context/TelegramContext';
import SettingsSection from './SettingsSection';

const NotificationToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ToggleLabel = styled.span`
  flex: 1;
  font-size: 14px;
  color: #4a6fa1;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
    
    &:checked + .slider {
      background: linear-gradient(145deg, #6a11cb, #2575fc);
    }
    
    &:checked + .slider:before {
      transform: translateX(24px);
    }
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #e0e5ec;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05),
                inset -2px -2px 4px rgba(255, 255, 255, 0.5);
    border-radius: 24px;
    transition: all 0.3s ease;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background: white;
      border-radius: 50%;
      transition: all 0.3s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    }
  }
`;

const NotificationsSection = () => {
  const { t } = useTranslation();
  const { telegramUserId } = useTelegram();
  const [notifications, setNotifications] = useState({
    daily: false,
    monthly: false
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchNotificationSettings();
  }, [telegramUserId]);

  const fetchNotificationSettings = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`https://fftg.farodinprojects.org:3000/get_notifications_settings?user_id=${telegramUserId}`);
      const data = await response.json();
      
      if (response.ok) {
        setNotifications({
          daily: data.daily_notifications,
          monthly: data.monthly_notifications
        });
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateNotificationSetting = async (type, isEnabled) => {
    // Оптимистичное обновление UI
    setNotifications(prev => ({ ...prev, [type]: isEnabled }));
    
    try {
      await fetch('https://fftg.farodinprojects.org:3000/update_notifications_settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: telegramUserId,
          notification_type: type,
          enabled: isEnabled
        })
      });
    } catch (error) {
      console.error('Error updating notification settings:', error);
      // Возвращаем предыдущее состояние в случае ошибки
      setNotifications(prev => ({ ...prev, [type]: !isEnabled }));
    }
  };

  return (
    <SettingsSection title={t('notification_settings')}>
      {isLoading ? (
        <p>{t('loading')}</p>
      ) : (
        <>
          <NotificationToggleContainer>
            <ToggleLabel>{t('daily_notifications')}</ToggleLabel>
            <ToggleSwitch>
              <input
                type="checkbox"
                checked={notifications.daily}
                onChange={(e) => updateNotificationSetting('daily', e.target.checked)}
              />
              <span className="slider"></span>
            </ToggleSwitch>
          </NotificationToggleContainer>
          
          <NotificationToggleContainer>
            <ToggleLabel>{t('monthly_notifications')}</ToggleLabel>
            <ToggleSwitch>
              <input
                type="checkbox"
                checked={notifications.monthly}
                onChange={(e) => updateNotificationSetting('monthly', e.target.checked)}
              />
              <span className="slider"></span>
            </ToggleSwitch>
          </NotificationToggleContainer>
        </>
      )}
    </SettingsSection>
  );
};

export default NotificationsSection;